import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { getCode, getName, getNames } from 'country-list';
import CampaignSelect from '@/components/common/form/CampaignSelect';
import ConstituentAutocomplete from '@/components/common/form/ConstituentAutocomplete';
import ControlledAssigneeSelect from '@/components/common/form/ControlledAssigneeSelect';
import ControlledDateInput from '@/components/common/form/ControlledDateInput';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import ControlledLifecycleBoardSelect from '@/components/common/form/ControlledLifecycleBoardSelect';
import ControlledLifecycleColumnSelect from '@/components/common/form/ControlledLifecycleColumnSelect';
import ControlledNumberInput from '@/components/common/form/ControlledNumberInput';
import ControlledSelect from '@/components/common/form/ControlledSelect';
import ControlledTextField from '@/components/common/form/ControlledTextField';
import DonationTypeSelect from '@/components/common/form/DonationTypeSelect';
import LabelSelect from '@/components/common/form/LabelSelect';
import Loader from '@/components/common/Loader';
import USAStateSelect from '@/components/common/USAStateSelect';
import BirthdateMonthOptions from '@/constants/BirthdateMonthOptions';
import ConstituentSearchFilterField from '@/constants/ConstituentSearchFilterField';
import CustomFieldTypes from '@/constants/customFields/CustomFieldTypes';
import FilterOperator from '@/constants/forms/FilterOperator';
import LoaderSizes from '@/constants/ui/LoaderSizes';
import { DONATION_RECURRING_CADENCES } from '@/lib/configs/contributions';
import translate from '@/lib/intl/translate';
import { ConstituentCategorySelect } from '@/modules/ConstituentListView';
import type CustomField from '@/types/api/CustomField';
import DefaultValueMap from '../constants/DefaultValueMap';
import FieldLabelMap from '../constants/FieldLabelMap';
import isMoneyField from '../utils/isMoneyField';

const {
  NAME_EMAIL_PHONE,
  CITY,
  POSTAL_CODE,
  STATE,
  COUNTRY,
  USER_IDS,
  CONSTITUENT_CATEGORIES,
  BIRTHDAY_MONTH,
  CONSTITUENT_LABEL_IDS,
  DOB,
  BOARD_IDS,
  COLUMN_IDS,
  CONNECTION_CONSTITUENT_IDS,
  CONNECTION_EMPLOYER_CONSTITUENT_IDS,
  CONNECTION_HOUSEHOLD_CONSTITUENT_IDS,
  IS_DECEASED,
  DONATION_TYPES,
  CAMPAIGN_IDS,
  DONATION_LIFETIME_TOTAL_AMOUNT,
  DONATION_THIS_YEAR_TOTAL_AMOUNT,
  DONATION_LAST_YEAR_TOTAL_AMOUNT,
  DONATION_AMOUNT,
  DONATION_LIFETIME_TOTAL_TIME,
  DONATION_THIS_YEAR_TOTAL_TIME,
  DONATION_LAST_YEAR_TOTAL_TIME,
  DONATION_MINUTES,
  DONATION_PLAN_CADENCE,
  IN_HONOR,
  DONATION_DATE,
  DONATION_MADE,
  DONATION_LABEL_IDS,
} = ConstituentSearchFilterField;

type AdvancedFilterValueInputProps = {
  uid?: string;
  operator: FilterOperator;
  field: ConstituentSearchFilterField | CustomFieldTypes;
  fieldArrayPrefix: string;
  customField?: CustomField;
};

function ValueLoader() {
  return (
    <div className="flex w-full items-center justify-center">
      <Loader size={LoaderSizes.SM} />
    </div>
  );
}

export default function AdvancedFilterValueInput({
  uid,
  field,
  operator,
  fieldArrayPrefix = '',
  customField,
}: AdvancedFilterValueInputProps) {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();
  const name = `${fieldArrayPrefix}value`;
  const watchedFieldValue = watch(name);

  const valueIsString = typeof watchedFieldValue === 'string';
  const valueIsArray = Array.isArray(watchedFieldValue);
  const valueIsBoolean = typeof watchedFieldValue === 'boolean';

  switch (field) {
    // Controllable text fields can be handled the same way
    case NAME_EMAIL_PHONE:
    case CITY:
    case POSTAL_CODE:
    case CustomFieldTypes.TEXT_MULTI:
    case CustomFieldTypes.TEXT_SINGLE:
      if (!valueIsString) {
        setValue(name, '');
        return <ValueLoader />;
      }

      return (
        <ControlledTextField
          className="w-full"
          control={control}
          defaultValue=""
          required
          id={`constituent-filter-value-${uid}`}
          InputLabelProps={{
            htmlFor: `constituent-filter-value-${uid}`,
          }}
          label={
            customField
              ? customField.fieldName
              : translate(FieldLabelMap[field as ConstituentSearchFilterField])
          }
          name={name}
        />
      );
    case STATE:
      if (!valueIsString) {
        setValue(name, '');
        return <ValueLoader />;
      }

      return (
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, value } }) => (
            <USAStateSelect
              className="w-full"
              onChange={onChange}
              value={value}
            />
          )}
        />
      );
    case COUNTRY: {
      if (!valueIsArray) {
        setValue(name, []);
        return <ValueLoader />;
      }

      const options = getNames().sort();
      return (
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, ...rest } }) => (
            <Autocomplete
              data-testid="advanced-filters-country-autocomplete"
              {...rest}
              onChange={(_, data: string[]) => {
                onChange(data.map((item) => getCode(item) || item));
              }}
              classes={{ root: 'w-full' }}
              options={options}
              multiple
              isOptionEqualToValue={(option: string, value: string) =>
                option === value || option === getName(value)
              }
              renderTags={(value: string[], getTagProps) =>
                value.map((option, index: number) => (
                  <Chip
                    label={getName(option)}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} />
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <TextField label={translate('COUNTRY_LABEL')} {...params} />
              )}
            />
          )}
        />
      );
    }
    case USER_IDS:
      return (
        <ControlledAssigneeSelect className="w-full" name={name} required />
      );
    case CONSTITUENT_CATEGORIES:
      if (!valueIsArray) {
        setValue(name, []);
        return <ValueLoader />;
      }

      return (
        <div className="grid w-full">
          <Controller
            control={control}
            name={name}
            render={({ field: { onChange, value } }) => (
              <ConstituentCategorySelect
                value={value}
                onChange={onChange}
                wrapValues
              />
            )}
          />
        </div>
      );
    case BIRTHDAY_MONTH:
      return (
        <FormControl className="w-full" size="small">
          <InputLabel id={`${uid}-month-label`} required>
            {translate('FORMS_BIRTHDATE_MONTH_LABEL')}
          </InputLabel>
          <ControlledSelect
            required
            labelId={`${uid}-month-label`}
            control={control}
            name={name}
            label={translate('FORMS_BIRTHDATE_MONTH_LABEL')}
            renderValue={(selected) =>
              selected ? (
                <div className="max-w-full truncate">
                  {
                    BirthdateMonthOptions.find(
                      ({ value }) => value === selected,
                    )?.text
                  }
                </div>
              ) : (
                ''
              )
            }
          >
            {BirthdateMonthOptions.map(({ value, text }) => (
              <MenuItem key={value} value={value}>
                {text}
              </MenuItem>
            ))}
          </ControlledSelect>
        </FormControl>
      );
    case CONSTITUENT_LABEL_IDS:
    case DONATION_LABEL_IDS:
      if (!valueIsArray) {
        setValue(name, []);
        return <ValueLoader />;
      }

      return (
        <div className="w-full">
          <Controller
            control={control}
            name={name}
            render={({ field: { onChange, value } }) => (
              <LabelSelect
                value={value}
                onChange={onChange}
                data-testid="advanced-filters-label-autocomplete"
                disableCloseOnSelect
              />
            )}
          />
        </div>
      );
    case DOB:
    case DONATION_DATE:
    case CustomFieldTypes.DATE_PICKER:
      if (operator === FilterOperator.BETWEEN) {
        return (
          <>
            <div className="w-1/2">
              <ControlledDateInput
                className="w-full"
                required
                control={control}
                label={translate('FORMS_START_DATE_LABEL')}
                name={`${name}.0`}
              />
            </div>
            <div className="w-1/2">
              <ControlledDateInput
                className="w-full"
                required
                control={control}
                label={translate('FORMS_END_DATE_LABEL')}
                name={`${name}.1`}
              />
            </div>
          </>
        );
      }

      return (
        <div className="w-full">
          <ControlledDateInput
            required
            control={control}
            label={translate('DATE_LABEL')}
            name={`${name}.0`}
          />
        </div>
      );
    case BOARD_IDS:
      if (!valueIsArray) {
        setValue(name, []);
        return <ValueLoader />;
      }

      return (
        <ControlledLifecycleBoardSelect
          className="w-full"
          multiple
          name={name}
          required
        />
      );
    case COLUMN_IDS:
      if (!valueIsArray) {
        setValue(name, []);
        return <ValueLoader />;
      }

      return (
        <ControlledLifecycleColumnSelect
          className="w-full"
          multiple
          name={name}
          required
        />
      );
    case IN_HONOR:
    case CONNECTION_CONSTITUENT_IDS:
    case CONNECTION_EMPLOYER_CONSTITUENT_IDS:
    case CONNECTION_HOUSEHOLD_CONSTITUENT_IDS: {
      const freeSolo = field === IN_HONOR;
      const label = field === IN_HONOR ? 'DEDICATED_TO' : 'CONNECTED_TO';

      return (
        <ConstituentAutocomplete
          className="w-2/3"
          label={translate(label)}
          name={name}
          required
          freeSolo={freeSolo}
        />
      );
    }
    case DONATION_TYPES:
      if (!valueIsArray) {
        setValue(name, []);
        return <ValueLoader />;
      }

      return (
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, value } }) => (
            <DonationTypeSelect onChange={onChange} value={value} required />
          )}
        />
      );
    case CAMPAIGN_IDS:
      if (!valueIsArray) {
        setValue(name, []);
        return <ValueLoader />;
      }

      return (
        <Controller
          control={control}
          name={name}
          render={({ field: { value, onChange } }) => (
            <CampaignSelect
              multiple
              onChange={onChange}
              value={value}
              required
            />
          )}
        />
      );
    case DONATION_LIFETIME_TOTAL_AMOUNT:
    case DONATION_THIS_YEAR_TOTAL_AMOUNT:
    case DONATION_LAST_YEAR_TOTAL_AMOUNT:
    case DONATION_AMOUNT:
    case DONATION_LIFETIME_TOTAL_TIME:
    case DONATION_THIS_YEAR_TOTAL_TIME:
    case DONATION_LAST_YEAR_TOTAL_TIME:
    case DONATION_MINUTES: {
      if (!valueIsArray) {
        setValue(name, []);
        return <ValueLoader />;
      }

      let firstInputLabel = 'DONATION_HOURS';
      if (isMoneyField(field)) {
        firstInputLabel = 'AMOUNT_LABEL';
      }
      if (operator === FilterOperator.BETWEEN) {
        firstInputLabel = 'FORMS_MIN_LABEL';
      }

      const firstInputName = `${name}.0`;
      const secondInputName = `${name}.1`;

      const prefix = isMoneyField(field) ? '$' : undefined;

      return (
        <div className="flex w-full gap-3">
          <ControlledNumberInput
            control={control}
            prefix={prefix}
            required
            id={`constituent-filter-value-${uid}`}
            label={translate(firstInputLabel)}
            name={firstInputName}
            error={errors?.[firstInputName]?.message}
            helperText={errors?.[firstInputName]?.message}
          />
          {operator === FilterOperator.BETWEEN && (
            <ControlledNumberInput
              control={control}
              prefix={prefix}
              required
              id={`constituent-filter-value-second-${uid}`}
              label={translate('FORMS_MAX_LABEL')}
              name={secondInputName}
              error={errors?.[secondInputName]?.message}
              helperText={errors?.[secondInputName]?.message}
            />
          )}
        </div>
      );
    }
    case DONATION_PLAN_CADENCE: {
      return (
        <FormControl className="w-full" size="small">
          <InputLabel id={`${uid}-plan-cadence-label`} required>
            {translate('RECURRING_CADENCE_LABEL')}
          </InputLabel>
          <ControlledSelect
            required
            labelId={`${uid}-plan-cadence-label`}
            control={control}
            name={name}
            label={translate('RECURRING_CADENCE_LABEL')}
          >
            {DONATION_RECURRING_CADENCES.map(({ value, text }) => (
              <MenuItem key={value} value={value}>
                {text}
              </MenuItem>
            ))}
          </ControlledSelect>
        </FormControl>
      );
    }
    case CustomFieldTypes.LIST_MULTI:
    case CustomFieldTypes.LIST_SINGLE:
      if (!customField || !valueIsArray) {
        setValue(name, []);
        return <ValueLoader />;
      }

      return (
        <FormControl className="w-full" size="small">
          <InputLabel id={`${uid}-cf-label`} required>
            {customField.fieldName}
          </InputLabel>
          <ControlledSelect
            required
            labelId={`${uid}-cf-label`}
            control={control}
            multiple
            name={name}
            label={customField.fieldName}
            renderValue={(selected) => (
              <div className="max-w-full truncate">
                {Array.isArray(selected) ? selected.join(', ') : ''}
              </div>
            )}
          >
            {(customField.fieldOptions || []).map((value) => (
              <MenuItem key={value} value={value}>
                {value}
                <Checkbox
                  className="ml-auto"
                  checked={
                    Array.isArray(watchedFieldValue) &&
                    watchedFieldValue.includes(value)
                  }
                />
              </MenuItem>
            ))}
          </ControlledSelect>
        </FormControl>
      );
    case DONATION_MADE:
    case IS_DECEASED:
    case CustomFieldTypes.CHECKBOX: {
      const isCustomFieldCheckbox = field === CustomFieldTypes.CHECKBOX;
      if (!valueIsBoolean) {
        setValue(name, DefaultValueMap[field]);
        return <ValueLoader />;
      }

      const label = isCustomFieldCheckbox
        ? customField?.fieldName
        : translate(FieldLabelMap[field]);
      const defaultValue = isCustomFieldCheckbox
        ? DefaultValueMap[CustomFieldTypes.CHECKBOX]
        : DefaultValueMap[field];

      return (
        <FormControl className="w-full" size="small">
          <InputLabel id={`${uid}-value-label`} required>
            {label}
          </InputLabel>
          <ControlledSelect
            defaultValue={defaultValue}
            required
            labelId={`${uid}-value-label`}
            control={control}
            name={name}
            label={label}
            renderValue={(selected) => (
              <div className="max-w-full truncate">{String(selected)}</div>
            )}
          >
            {[true, false].map((value) => (
              // See https://stackoverflow.com/a/72563809 for using a boolean as a value
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              <MenuItem key={String(value)} value={value as any}>
                {String(value)}
              </MenuItem>
            ))}
          </ControlledSelect>
        </FormControl>
      );
    }
    default:
      return null;
  }
}
